package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Add
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.TextButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.data.PickedMediaHolder
import org.gymly.model.AdminCompany
import org.gymly.model.AdminCompanyRepresentativeUpsert
import org.gymly.model.AdminCompanyUpsert
import org.gymly.model.AdminSettingsModules
import org.gymly.model.AdminSettingsModulesUpsert
import org.gymly.model.PickedImagePayload
import org.gymly.theme.AdminColors
import org.gymly.ui.components.FileUploadIconButton
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.util.GYMLY_TIMEZONE_ID
import org.gymly.util.openImagePicker
import org.gymly.util.userMessage

private data class CompanyCountryTimezone(
    val timezoneId: String,
    val countryLabel: String,
    val offsetLabel: String,
) {
    fun menuLabel(): String = "$countryLabel · $timezoneId ($offsetLabel)"
}

/** Países / zonas disponibles en Configuración → Empresa. */
private val COMPANY_COUNTRY_TIMEZONES = listOf(
    CompanyCountryTimezone(GYMLY_TIMEZONE_ID, "Venezuela", "UTC−04:00"),
)

@Composable
fun SettingsCompanyPanel(
    compact: Boolean,
    modules: AdminSettingsModules = AdminSettingsModules(accessEnabled = true, exchangeRatesEnabled = true),
    onModulesChange: (AdminSettingsModules) -> Unit = {},
) {
    val scope = rememberCoroutineScope()
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var picking by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var company by remember { mutableStateOf<AdminCompany?>(null) }
    var name by remember { mutableStateOf("") }
    var address by remember { mutableStateOf("") }
    var taxId by remember { mutableStateOf("") }
    var brandColor by remember { mutableStateOf("") }
    var showLoginClients by remember { mutableStateOf(true) }
    var showLoginTrainers by remember { mutableStateOf(true) }
    var showLoginOnTime by remember { mutableStateOf(true) }
    var termsMonitoringOn by remember { mutableStateOf(false) }
    var termsMonitoringAvailable by remember { mutableStateOf(false) }
    var termsNoticeCooldown by remember { mutableStateOf("3") }
    var timezone by remember { mutableStateOf(GYMLY_TIMEZONE_ID) }
    var timezoneMenu by remember { mutableStateOf(false) }
    var reps by remember { mutableStateOf(listOf(RepDraft())) }
    var repsOpen by remember { mutableStateOf(false) }
    var hoursDrafts by remember { mutableStateOf(initialHoursDayDrafts(null)) }
    var logoName by remember { mutableStateOf<String?>(null) }
    var bannerName by remember { mutableStateOf<String?>(null) }
    var mediaRevision by remember { mutableStateOf(0) }
    var savingModules by remember { mutableStateOf(false) }
    val logoHolder = remember { PickedMediaHolder() }
    val bannerHolder = remember { PickedMediaHolder() }

    fun formSnapshot(): String = listOf(
        name, address, taxId, brandColor, timezone,
        showLoginClients.toString(), showLoginTrainers.toString(), showLoginOnTime.toString(),
        termsMonitoringOn.toString(), termsNoticeCooldown,
        reps.toString(), hoursDrafts.toString(),
        (logoHolder.payload != null).toString(), (bannerHolder.payload != null).toString(),
    ).joinToString("|")
    var savedSnapshot by remember { mutableStateOf<String?>(null) }
    val hasChanges = savedSnapshot != null && formSnapshot() != savedSnapshot

    fun applyCompany(c: AdminCompany) {
        company = c
        name = c.name
        address = c.address.orEmpty()
        taxId = c.taxId.orEmpty()
        brandColor = c.brandColor.orEmpty()
        showLoginClients = c.loginShowClients
        showLoginTrainers = c.loginShowTrainers
        showLoginOnTime = c.loginShowOnTime
        termsMonitoringOn = c.termsMonitoringEnabled
        termsMonitoringAvailable = c.termsMonitoringAvailable
        termsNoticeCooldown = c.termsNoticeCooldownDays.toString()
        timezone =c.timezone.ifBlank { GYMLY_TIMEZONE_ID }.let { tz ->
            COMPANY_COUNTRY_TIMEZONES.firstOrNull { it.timezoneId == tz }?.timezoneId ?: GYMLY_TIMEZONE_ID
        }
        org.gymly.theme.BrandTheme.apply(c.brandColor)
        reps = c.representatives
            .sortedBy { it.sortOrder }
            .map {
                RepDraft(
                    name = it.name,
                    title = it.title.orEmpty(),
                    docId = it.docId.orEmpty(),
                    staffId = it.staffId,
                )
            }
            .ifEmpty { listOf(RepDraft()) }
        repsOpen = c.representatives.any { it.name.isNotBlank() }
        hoursDrafts = initialHoursDayDrafts(c.operatingHours)
        logoName = if (c.hasLogo) "Logo actual" else null
        bannerName = if (c.hasBanner) "Banner actual" else null
        logoHolder.clear()
        bannerHolder.clear()
        mediaRevision++
        savedSnapshot = formSnapshot()
    }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                applyCompany(AdminRepo.api.getCompany())
            } catch (e: Throwable) {
                error = e.userMessage("No se pudo cargar la empresa.")
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(Unit) { reload() }

    when {
        loading -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            CircularProgressIndicator(color = AdminColors.Primary)
        }
        else -> Column(Modifier.fillMaxSize()) {
        VerticalScrollColumn(
            Modifier.weight(1f).fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            error?.let { Text(it, color = AdminColors.Error, fontSize = 13.sp) }

            Column(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(16.dp))
                    .background(AdminColors.SurfaceContainer)
                    .padding(20.dp),
            ) {
                Text("Datos de la empresa", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = AdminColors.Text)
                Text(
                    "Nombre, dirección, RIF y zona horaria de la instalación.",
                    color = AdminColors.TextMuted,
                    fontSize = 13.sp,
                    modifier = Modifier.padding(top = 4.dp, bottom = 16.dp),
                )
                Text("Nombre de la empresa *", color = AdminColors.TextMuted, fontSize = 12.sp)
                Spacer(Modifier.height(4.dp))
                OutlinedTextField(
                    value = name,
                    onValueChange = { name = it },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = RoundedCornerShape(10.dp),
                    colors = companyFieldColors(),
                )

                Spacer(Modifier.height(12.dp))
                val dirField: @Composable () -> Unit = {
                Text("Dirección", color = AdminColors.TextMuted, fontSize = 12.sp)
                Spacer(Modifier.height(4.dp))
                OutlinedTextField(
                    value = address,
                    onValueChange = { address = it },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = RoundedCornerShape(10.dp),
                    colors = companyFieldColors(),
                )
                }
                val rifField: @Composable () -> Unit = {
                Text("RIF / identificación fiscal", color = AdminColors.TextMuted, fontSize = 12.sp)
                Spacer(Modifier.height(4.dp))
                OutlinedTextField(
                    value = taxId,
                    onValueChange = { taxId = it },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    placeholder = { Text("Ej. J-12345678-9", color = AdminColors.TextMuted) },
                    shape = RoundedCornerShape(10.dp),
                    colors = companyFieldColors(),
                )
                }
                BoxWithConstraints(Modifier.fillMaxWidth()) {
                    if (maxWidth < 600.dp) {
                        Column(Modifier.fillMaxWidth()) {
                            dirField()
                            Spacer(Modifier.height(12.dp))
                            rifField()
                        }
                    } else {
                        Row(
                            Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp),
                            verticalAlignment = Alignment.Top,
                        ) {
                            Column(Modifier.weight(1f)) { dirField() }
                            Column(Modifier.weight(1f)) { rifField() }
                        }
                    }
                }
                Spacer(Modifier.height(12.dp))
                Text("País / zona horaria", color = AdminColors.TextMuted, fontSize = 12.sp)
                Spacer(Modifier.height(4.dp))
                Box {
                    OutlinedButton(
                        onClick = { timezoneMenu = true },
                        enabled = !saving && !picking,
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(10.dp),
                    ) {
                        Text(
                            COMPANY_COUNTRY_TIMEZONES
                                .firstOrNull { it.timezoneId == timezone }
                                ?.menuLabel()
                                ?: "Venezuela · $GYMLY_TIMEZONE_ID (UTC−04:00)",
                            color = AdminColors.Text,
                        )
                    }
                    DropdownMenu(
                        expanded = timezoneMenu,
                        onDismissRequest = { timezoneMenu = false },
                    ) {
                        COMPANY_COUNTRY_TIMEZONES.forEach { option ->
                            DropdownMenuItem(
                                text = { Text(option.menuLabel()) },
                                onClick = {
                                    timezone = option.timezoneId
                                    timezoneMenu = false
                                },
                            )
                        }
                    }
                }
                Text(
                    "Define el país operativo. Fechas, cobros, reportes y notificaciones usan su zona (local y servidor).",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(top = 4.dp),
                )
            }

            Column(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(16.dp))
                    .background(AdminColors.SurfaceContainer)
                    .padding(20.dp),
            ) {
                Text("Marca", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = AdminColors.Text)
                Text(
                    "Logo, foto del inicio de sesión y color principal del sitio.",
                    color = AdminColors.TextMuted,
                    fontSize = 13.sp,
                    modifier = Modifier.padding(top = 4.dp, bottom = 16.dp),
                )
                Text("Logo", color = AdminColors.TextMuted, fontSize = 12.sp)
                Spacer(Modifier.height(4.dp))
                CompanyImagePick(
                    uploadTooltip = if (logoName != null) "Cambiar logo" else "Subir logo",
                    initials = company?.initials() ?: "E",
                    remoteUrl = company?.logoUrl?.takeIf { company?.hasLogo == true },
                    localBytes = logoHolder.payload?.bytes,
                    revision = mediaRevision,
                    enabled = !saving && !picking,
                    onClick = {
                        picking = true
                        openImagePicker { picked ->
                            scope.launch {
                                picking = false
                                if (picked == null) {
                                    error = "No se seleccionó ninguna imagen."
                                    return@launch
                                }
                                val payload = PickedImagePayload(picked.bytes, picked.fileName, picked.mimeType)
                                logoHolder.set(payload, picked.fileName)
                                logoName = picked.fileName
                                mediaRevision++
                                error = null
                            }
                        }
                    },
                )

                Spacer(Modifier.height(12.dp))
                Text("Foto del inicio de sesión", color = AdminColors.TextMuted, fontSize = 12.sp)
                Text(
                    "Se muestra a la izquierda del login. Si no hay foto, queda la imagen por defecto. También se usa como banner de la app.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(top = 2.dp, bottom = 4.dp),
                )
                Spacer(Modifier.height(4.dp))
                CompanyImagePick(
                    uploadTooltip = if (bannerName != null) "Cambiar banner" else "Subir banner",
                    initials = "B",
                    remoteUrl = company?.bannerUrl?.takeIf { company?.hasBanner == true },
                    localBytes = bannerHolder.payload?.bytes,
                    revision = mediaRevision,
                    enabled = !saving && !picking,
                    onClick = {
                        picking = true
                        openImagePicker { picked ->
                            scope.launch {
                                picking = false
                                if (picked == null) {
                                    error = "No se seleccionó ninguna imagen."
                                    return@launch
                                }
                                val payload = PickedImagePayload(picked.bytes, picked.fileName, picked.mimeType)
                                bannerHolder.set(payload, picked.fileName)
                                bannerName = picked.fileName
                                mediaRevision++
                                error = null
                            }
                        }
                    },
                )

                Spacer(Modifier.height(12.dp))
                Text("Color del sitio", color = AdminColors.TextMuted, fontSize = 12.sp)
                Spacer(Modifier.height(4.dp))
                OutlinedTextField(
                    value = brandColor,
                    onValueChange = { brandColor = it.take(7) },
                    modifier = Modifier.widthIn(max = 240.dp),
                    singleLine = true,
                    placeholder = { Text("#1E2A78", color = AdminColors.TextMuted) },
                    shape = RoundedCornerShape(10.dp),
                    colors = companyFieldColors(),
                )
                Text(
                    "Color principal de botones y del inicio de sesión, para que combine con el logo.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(top = 4.dp),
                )

            }

            Column(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(16.dp))
                    .background(AdminColors.SurfaceContainer)
                    .padding(20.dp),
            ) {
                Text("Personal y estadísticas", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = AdminColors.Text)
                Text(
                    "Avisos de términos y cifras que se muestran en el inicio de sesión.",
                    color = AdminColors.TextMuted,
                    fontSize = 13.sp,
                    modifier = Modifier.padding(top = 4.dp, bottom = 16.dp),
                )
                // Solo si la licencia incluye portal o app (web, Android o iOS).
                if (termsMonitoringAvailable) {
                    Spacer(Modifier.height(12.dp))
                    Text("Personal", color = AdminColors.TextMuted, fontSize = 12.sp)
                    Spacer(Modifier.height(4.dp))
                    LoginStatCheckbox(
                        "Monitoreo de Términos y condiciones",
                        termsMonitoringOn,
                        enabled = !saving,
                    ) { termsMonitoringOn = it }
                    Text(
                        "Muestra en Configuración quién aceptó o rechazó los términos y compromisos de los socios, " +
                            "y permite avisarles y reactivarlos.",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                    Spacer(Modifier.height(8.dp))
                    Text("Días mínimos entre dos avisos al mismo socio", color = AdminColors.TextMuted, fontSize = 12.sp)
                    OutlinedTextField(
                        value = termsNoticeCooldown,
                        onValueChange = { termsNoticeCooldown = it.filter(Char::isDigit).take(2) },
                        enabled = !saving,
                        singleLine = true,
                        modifier = Modifier.widthIn(max = 180.dp),
                        placeholder = { Text("0 a 90") },
                    )
                }
                Spacer(Modifier.height(12.dp))
                Text("Estadísticas del inicio de sesión", color = AdminColors.TextMuted, fontSize = 12.sp)
                Spacer(Modifier.height(4.dp))
                LoginStatCheckbox("Clientes activos", showLoginClients, enabled = !saving) { showLoginClients = it }
                LoginStatCheckbox("Entrenadores", showLoginTrainers, enabled = !saving) { showLoginTrainers = it }
                LoginStatCheckbox("Pagos a tiempo", showLoginOnTime, enabled = !saving) { showLoginOnTime = it }
                Text(
                    "Cifras que se muestran en el pie del inicio de sesión.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(top = 4.dp),
                )

            }

            Column(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(16.dp))
                    .background(AdminColors.SurfaceContainer)
                    .padding(20.dp),
            ) {
                Text("Módulos", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = AdminColors.Text)
                Text(
                    "Activa o desactiva integraciones del gimnasio. Al desactivar, se ocultan pestañas, widgets y funciones relacionadas.",
                    color = AdminColors.TextMuted,
                    fontSize = 13.sp,
                    modifier = Modifier.padding(top = 4.dp, bottom = 12.dp),
                )
                ModuleToggleRow(
                    label = "Control de acceso (Hikvision)",
                    description = "Entrada de socios con huella o rostro. Si está apagado, se ocultan las pantallas de acceso.",
                    checked = modules.accessEnabled,
                    enabled = !savingModules,
                    onCheckedChange = { checked ->
                        scope.launch {
                            savingModules = true
                            try {
                                val updated = AdminRepo.api.updateSettingsModules(
                                    AdminSettingsModulesUpsert(
                                        accessEnabled = checked,
                                        exchangeRatesEnabled = modules.exchangeRatesEnabled,
                                        suppressAccessWhileCapturing = modules.suppressAccessWhileCapturing,
                                        clientAccountsEnabled = modules.clientAccountsEnabled,
                                        schedulesAutomatic = modules.schedulesAutomatic,
                                    ),
                                )
                                onModulesChange(updated)
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudo actualizar el módulo de acceso.")
                            } finally {
                                savingModules = false
                            }
                        }
                    },
                )
                ModuleToggleRow(
                    label = "Monedero digital",
                    description = "Si está apagado: no se acredita excedente al monedero, no se aplica saldo en cobros y se oculta su menú.",
                    checked = modules.clientAccountsEnabled,
                    enabled = !savingModules,
                    onCheckedChange = { checked ->
                        scope.launch {
                            savingModules = true
                            try {
                                val updated = AdminRepo.api.updateSettingsModules(
                                    AdminSettingsModulesUpsert(
                                        accessEnabled = modules.accessEnabled,
                                        exchangeRatesEnabled = modules.exchangeRatesEnabled,
                                        suppressAccessWhileCapturing = modules.suppressAccessWhileCapturing,
                                        clientAccountsEnabled = checked,
                                        schedulesAutomatic = modules.schedulesAutomatic,
                                    ),
                                )
                                onModulesChange(updated)
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudo actualizar el módulo de monedero digital.")
                            } finally {
                                savingModules = false
                            }
                        }
                    },
                )
                Spacer(Modifier.height(8.dp))
                ModuleToggleRow(
                    label = "Horarios automáticos (entrenadores)",
                    description = if (modules.schedulesAutomatic) {
                        "Cada semana fija el piso abierto de cada entrenador según su jornada."
                    } else {
                        "Los horarios se gestionan solo en Horarios, sin generarlos desde la jornada del trabajador."
                    },
                    checked = modules.schedulesAutomatic,
                    enabled = !savingModules,
                    onCheckedChange = { checked ->
                        scope.launch {
                            savingModules = true
                            try {
                                val updated = AdminRepo.api.updateSettingsModules(
                                    AdminSettingsModulesUpsert(
                                        accessEnabled = modules.accessEnabled,
                                        exchangeRatesEnabled = modules.exchangeRatesEnabled,
                                        suppressAccessWhileCapturing = modules.suppressAccessWhileCapturing,
                                        clientAccountsEnabled = modules.clientAccountsEnabled,
                                        schedulesAutomatic = checked,
                                    ),
                                )
                                onModulesChange(updated)
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudo actualizar los horarios automáticos.")
                            } finally {
                                savingModules = false
                            }
                        }
                    },
                )
            }

            Column(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(16.dp))
                    .background(AdminColors.SurfaceContainer)
                    .padding(20.dp),
            ) {
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Column {
                        Text("Representantes", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                        Text(
                            "Opcional: personas que representan a la empresa.",
                            color = AdminColors.TextMuted,
                            fontSize = 12.sp,
                        )
                    }
                    TextButton(onClick = { repsOpen = !repsOpen }) {
                        Text(if (repsOpen) "Ocultar" else "Mostrar", color = AdminColors.Primary)
                    }
                }
                if (repsOpen) {
                    Spacer(Modifier.height(12.dp))
                    OutlinedButton(
                        onClick = { reps = reps + RepDraft() },
                        enabled = !saving,
                        shape = RoundedCornerShape(10.dp),
                    ) {
                        Icon(Icons.Outlined.Add, null, tint = AdminColors.Primary)
                        Spacer(Modifier.width(6.dp))
                        Text("Agregar representante", color = AdminColors.Primary)
                    }
                    Spacer(Modifier.height(12.dp))
                    reps.forEachIndexed { index, rep ->
                    Column(
                        Modifier
                            .fillMaxWidth()
                            .padding(bottom = 12.dp)
                            .clip(RoundedCornerShape(12.dp))
                            .border(1.dp, AdminColors.Outline, RoundedCornerShape(12.dp))
                            .padding(12.dp),
                    ) {
                        Row(
                            Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Text("Representante ${index + 1}", fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
                            if (reps.size > 1) {
                                IconButton(
                                    onClick = { reps = reps.filterIndexed { i, _ -> i != index } },
                                    enabled = !saving,
                                ) {
                                    Icon(Icons.Outlined.Delete, "Quitar", tint = AdminColors.Error)
                                }
                            }
                        }
                        OutlinedTextField(
                            value = rep.name,
                            onValueChange = { v ->
                                reps = reps.toMutableList().also { it[index] = it[index].copy(name = v) }
                            },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true,
                            placeholder = { Text("Nombre", color = AdminColors.TextMuted) },
                            shape = RoundedCornerShape(10.dp),
                            colors = companyFieldColors(),
                        )
                        Spacer(Modifier.height(8.dp))
                        OutlinedTextField(
                            value = rep.docId,
                            onValueChange = { v ->
                                reps = reps.toMutableList().also { it[index] = it[index].copy(docId = v) }
                            },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true,
                            placeholder = { Text("Cédula / DNI", color = AdminColors.TextMuted) },
                            shape = RoundedCornerShape(10.dp),
                            colors = companyFieldColors(),
                        )
                        Spacer(Modifier.height(8.dp))
                        OutlinedTextField(
                            value = rep.title,
                            onValueChange = { v ->
                                reps = reps.toMutableList().also { it[index] = it[index].copy(title = v) }
                            },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true,
                            placeholder = { Text("Cargo / título", color = AdminColors.TextMuted) },
                            shape = RoundedCornerShape(10.dp),
                            colors = companyFieldColors(),
                        )
                    }
                }
                }
            }

            Column(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(16.dp))
                    .background(AdminColors.SurfaceContainer)
                    .padding(20.dp),
            ) {
                Text("Horario de atención", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = AdminColors.Text)
                Text(
                    "Define los días y franjas en que abre el gimnasio. Puedes dejar huecos (p. ej. 06:00–12:00 y 14:00–21:00). La disponibilidad de todos los trabajadores se valida obligatoriamente contra este horario.",
                    color = AdminColors.TextMuted,
                    fontSize = 13.sp,
                    modifier = Modifier.padding(top = 4.dp, bottom = 12.dp),
                )
                OperatingHoursEditor(
                    dayDrafts = hoursDrafts,
                    onDayDraftsChange = { hoursDrafts = it },
                    enabled = !saving,
                    hint = "Formato 24 h (HH:mm). Marca el día y agrega uno o más bloques.",
                )
            }

        }
            Column(
                Modifier
                    .fillMaxWidth()
                    .background(AdminColors.SurfaceContainer)
                    .padding(horizontal = 20.dp, vertical = 12.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Text(
                    if (hasChanges) "Tienes cambios sin guardar." else "Todos los cambios están guardados.",
                    color = if (hasChanges) Color(0xFFE65100) else AdminColors.TextMuted,
                    fontSize = 13.sp,
                    fontWeight = if (hasChanges) FontWeight.SemiBold else FontWeight.Normal,
                )
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = if (compact) Arrangement.Center else Arrangement.End,
            ) {
                Button(
                    onClick = {
                        if (name.trim().isEmpty()) {
                            error = "El nombre es obligatorio."
                            return@Button
                        }
                        val color = brandColor.trim()
                        if (color.isNotEmpty() && !color.matches(Regex("#[0-9A-Fa-f]{6}"))) {
                            error = "El color debe ser hexadecimal, por ejemplo #1E2A78."
                            return@Button
                        }
                        val (hours, hoursError) = buildHoursSchedule(hoursDrafts)
                        if (hoursError != null) {
                            error = hoursError
                            return@Button
                        }
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                val saved = AdminRepo.api.updateCompany(
                                    AdminCompanyUpsert(
                                        name = name.trim(),
                                        address = address.trim().ifBlank { null },
                                        taxId = taxId.trim().ifBlank { null },
                                        brandColor = brandColor.trim().ifBlank { null },
                                        timezone = timezone.ifBlank { GYMLY_TIMEZONE_ID },
                                        representatives = reps
                                            .map {
                                                AdminCompanyRepresentativeUpsert(
                                                    name = it.name.trim(),
                                                    title = it.title.trim().ifBlank { null },
                                                    docId = it.docId.trim().ifBlank { null },
                                                    staffId = it.staffId,
                                                )
                                            }
                                            .filter { it.name.isNotEmpty() },
                                        operatingHours = hours.orEmpty(),
                                        logo = logoHolder.payload,
                                        banner = bannerHolder.payload,
                                        loginShowClients = showLoginClients,
                                        loginShowTrainers = showLoginTrainers,
                                        loginShowOnTime = showLoginOnTime,
                                        termsMonitoringEnabled = termsMonitoringOn,
                                        termsNoticeCooldownDays = termsNoticeCooldown.toIntOrNull() ?: 3,
                                    ),
                                )
                                applyCompany(saved)
                            } catch (e: Throwable) {
                                error = e.userMessage("No se pudo guardar la empresa.")
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving && !picking,
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "Guardando…" else "Guardar empresa", fontWeight = FontWeight.SemiBold)
                }
            }
            }
        }
    }
}

private data class RepDraft(
    val name: String = "",
    val title: String = "",
    val docId: String = "",
    val staffId: String? = null,
)

@Composable
private fun LoginStatCheckbox(label: String, checked: Boolean, enabled: Boolean, onChange: (Boolean) -> Unit) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Checkbox(checked = checked, onCheckedChange = onChange, enabled = enabled)
        Text(label, color = AdminColors.Text, fontSize = 13.sp)
    }
}

@Composable
private fun companyFieldColors() = OutlinedTextFieldDefaults.colors(
    focusedBorderColor = AdminColors.Primary,
    unfocusedBorderColor = AdminColors.Outline,
)

@Composable
private fun CompanyImagePick(
    uploadTooltip: String,
    initials: String,
    remoteUrl: String?,
    localBytes: ByteArray?,
    revision: Int,
    enabled: Boolean,
    onClick: () -> Unit,
) {
    val previewModel: Any? = when {
        localBytes != null && localBytes.isNotEmpty() -> localBytes
        !remoteUrl.isNullOrBlank() -> {
            val sep = if (remoteUrl.contains('?')) '&' else '?'
            "$remoteUrl${sep}v=$revision"
        }
        else -> null
    }

    Row(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Outline, RoundedCornerShape(12.dp))
            .padding(12.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            Modifier
                .size(64.dp)
                .clip(RoundedCornerShape(10.dp))
                .background(AdminColors.Surface),
            contentAlignment = Alignment.Center,
        ) {
            if (previewModel != null) {
                key(previewModel, revision) {
                    AsyncImage(
                        model = previewModel,
                        contentDescription = uploadTooltip,
                        modifier = Modifier.fillMaxSize().padding(4.dp),
                        contentScale = ContentScale.Fit,
                    )
                }
            } else {
                Text(initials.take(2).ifBlank { "?" }, color = AdminColors.Primary, fontWeight = FontWeight.Bold)
            }
        }
        Spacer(Modifier.width(12.dp))
        FileUploadIconButton(
            onClick = onClick,
            tooltip = uploadTooltip,
            enabled = enabled,
            tint = AdminColors.Primary,
        )
    }
}

@Composable
private fun ModuleToggleRow(
    label: String,
    description: String,
    checked: Boolean,
    enabled: Boolean,
    onCheckedChange: (Boolean) -> Unit,
) {
    val stateColor = if (checked) Color(0xFF2E7D32) else AdminColors.TextMuted
    Row(
        Modifier.fillMaxWidth().padding(vertical = 6.dp),
        verticalAlignment = Alignment.Top,
    ) {
        Checkbox(
            checked = checked,
            onCheckedChange = onCheckedChange,
            enabled = enabled,
            colors = CheckboxDefaults.colors(
                checkedColor = AdminColors.Primary,
                checkmarkColor = AdminColors.OnBrand,
            ),
        )
        Column(Modifier.weight(1f).padding(top = 12.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(label, color = AdminColors.Text, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
                Text(
                    if (checked) "Activo" else "Desactivado",
                    color = stateColor,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier
                        .background(stateColor.copy(alpha = 0.12f), RoundedCornerShape(8.dp))
                        .padding(horizontal = 8.dp, vertical = 2.dp),
                )
            }
            Text(
                description,
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 2.dp),
            )
        }
    }
}
